import { type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/providers/auth-provider'
import { Loader2 } from 'lucide-react'

type ProtectedLayoutProps = {
  children: ReactNode
}

/**
 * When auth is configured (VITE_SUPABASE_* set), requires a session and redirects to /login if missing.
 * When auth is not configured, renders children (single-user mode).
 */
export function ProtectedLayout({ children }: ProtectedLayoutProps) {
  const { session, isLoading, isAuthConfigured } = useAuth()
  const location = useLocation()

  if (!isAuthConfigured) {
    return <>{children}</>
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!session) {
    const returnPath = `${location.pathname}${location.search}`
    const loginPath =
      returnPath && returnPath !== '/login'
        ? `/login?redirect=${encodeURIComponent(returnPath)}`
        : '/login'
    return <Navigate to={loginPath} replace />
  }

  return <>{children}</>
}
